import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import PostCard from './PostCard';
import PostComposer from './PostComposer';
import { toast } from 'sonner';
import { MessageCircle, Loader2 } from 'lucide-react';
import EditPostModal from './EditPostModal';
import ShareModal from './ShareModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Brief LOT2.1 — le mur de profil rend désormais les publications via le
   composant PostCard COMMUN (le même que le Feed Actualité). Fini les
   divergences de rendu entre les deux pages.
   Brief 27 oct A.3 (RCA) — le composeur aussi est désormais COMMUN
   (PostComposer) : média, lien, sondage, produit, communauté à l'identique. */
const ProfileWall = ({ userId, isOwnProfile }) => {
  const { user: currentUser } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedComments, setExpandedComments] = useState({});
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [sharePost, setSharePost] = useState(null);
  const [editPost, setEditPost] = useState(null);
  const [bookedEventIds, setBookedEventIds] = useState(() => new Set());

  useEffect(() => {
    let alive = true;
    if (!currentUser?.user_id) { setBookedEventIds(new Set()); return; }
    axios.get(`${API}/bookings?role=client`, { withCredentials: true })
      .then(({ data }) => {
        if (!alive) return;
        const ids = new Set();
        (data?.bookings || []).forEach((b) => {
          if (b.event_id && ['confirmed', 'pending'].includes(b.status)) ids.add(b.event_id);
        });
        setBookedEventIds(ids);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [currentUser?.user_id]);

  const loadPosts = useCallback(async (reset = false) => {
    try {
      const skip = reset ? 0 : page * 10;
      const r = await axios.get(`${API}/users/${userId}/posts?skip=${skip}&limit=10`, { withCredentials: true });
      const newPosts = r.data.posts || [];
      if (reset) { setPosts(newPosts); setPage(1); }
      else { setPosts(prev => [...prev, ...newPosts]); setPage(p => p + 1); }
      setHasMore(newPosts.length === 10);
    } catch (e) { console.error('Wall error:', e); }
    finally { setLoading(false); }
  }, [userId, page]);

  useEffect(() => { setLoading(true); setPosts([]); setPage(0); loadPosts(true); }, [userId]);

  const handleDelete = async (postId) => {
    if (!window.confirm('Supprimer cette publication ?')) return;
    try {
      await axios.delete(`${API}/posts/${postId}`, { withCredentials: true });
      setPosts(prev => prev.filter(p => p.post_id !== postId));
      toast.success('Publication supprimée');
    } catch (e) { toast.error('La suppression a échoué'); }
  };

  return (
    <div className="space-y-4" data-testid="profile-wall">
      {/* Composeur COMMUN Feed + Profil (A.3) */}
      {isOwnProfile && (
        <PostComposer variant="wall" onPosted={() => loadPosts(true)} placeholder="Quoi de neuf ? Partage avec ton réseau…" />
      )}

      {/* Posts — rendu unifié via PostCard (Brief LOT2.1) */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-purple-500" /></div>
      ) : posts.length === 0 ? (
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardContent className="py-8 text-center" data-testid="wall-empty">
            <MessageCircle className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              {isOwnProfile ? 'Tu n’as encore rien publié — ta première publication apparaîtra ici et dans l’Actualité.' : 'Aucune publication pour le moment'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {posts.map(post => (
            <PostCard
              key={post.post_id}
              post={post}
              variant="wall"
              currentUser={currentUser}
              bookedEventIds={bookedEventIds}
              canManage={isOwnProfile || post.user_id === currentUser?.user_id}
              onDelete={handleDelete}
              onShare={(p) => setSharePost(p)}
              onEdit={(p) => setEditPost(p)}
              expandedComments={expandedComments}
              setExpandedComments={setExpandedComments}
            />
          ))}
          {hasMore && (
            <div className="text-center">
              <Button variant="outline" onClick={() => loadPosts(false)} data-testid="wall-load-more">
                Voir plus de publications
              </Button>
            </div>
          )}
        </>
      )}

      {sharePost && (
        <ShareModal
          isOpen={!!sharePost}
          onClose={() => setSharePost(null)}
          shareType="post"
          shareData={{
            id: sharePost.post_id,
            title: `Publication de ${sharePost.author_name || sharePost.display_name || 'OpenSapiens'}`,
            description: sharePost.content?.substring(0, 100) + (sharePost.content?.length > 100 ? '...' : ''),
          }}
        />
      )}

      {editPost && (
        <EditPostModal
          isOpen={!!editPost}
          post={editPost}
          onClose={() => setEditPost(null)}
          onSaved={(updated) => {
            setPosts((prev) => prev.map((p) => p.post_id === updated.post_id ? { ...p, ...updated } : p));
            setEditPost(null);
          }}
        />
      )}
    </div>
  );
};

export default ProfileWall;
